import { Fragment, useEffect, useState } from "react";
import Div from "../../../components/base/Div";
import Indicator from "../../../components/base/Indicator";
import Section from "../../../components/base/Section";
import Layout from "../../../components/layouts/Layout";
import { getOwner, getUser, hasRole, setPage, useHub, useScreen } from "../../../core/Global";
import { API_ROUTES } from "../../../core/Constants";
import { Link, useParams } from "react-router-dom";
import User from "../models/User";
import AccountOverview from "./AccountOverview";
import AccountTechSettings from "../components/AccountTechSettings";
import Icon from "../../../components/base/Icon";
import AccountEditForm from "../../../components/account/AccountEditForm";
import { UserTypes } from "../../../core/Enums";
import AccountTimezone from "../components/AccountTimezone";
import AccountPBXServer from "../components/AccountPBXServer";
import AccountTechAddressMetro from "../components/AccountTechAddressMetro";
import AccountSecurity from "../components/AccountSecurity";
import AccountTechSendJobSettings from "../components/AccountTechSendJobSettings";
import CustomersSettings from "../components/CustomersSettings";
import JobsCalendar from "../../../components/calendar/JobsCalendar";
import AccountTechLiveLocation from "../components/AccountTechLiveLocation";
import AccountTechAutoClock from "../components/AccountTechAutoClock";
import RolesEditor from "../components/RolesEditor";
import NotificationsView from "../../system/settings/views/NotificationsView";
import TechWorkingHours from "../components/TechWorkingHours";
import FlexRow from "../../../components/base/FlexRow";
import ScrollContainer from "../../../components/base/ScrollContainer";
import CallsAccess from "../components/CallsAccess";
import AccountUserGroups from "../components/AccountUserGroups";
import AccountPlan from "../components/AccountPlan";
import { userRoleCategories } from "../utils/roleCategories";
import "../components/UserGroupRoles.css";


interface IPageLink {
    name: string
    icon: any
    slug: string
    default?: boolean
}

export default function AccountDetails() {

    const hub = useHub()
    const user = getUser()
    const screen = useScreen()
    const { selectedId, selectedPage, selectedAction } = useParams();
    const [fetching, fetchingSet] = useState<boolean>(true);
    const [fetched, fetchedSet] = useState<boolean>(false);
    const [model, modelSet] = useState<User>();
    const [pages, pagesSet] = useState<IPageLink[]>([]);

    // Who may see and change another user's notification settings.
    const canManageNotifications = !!user?.isSuperAdmin || !!user?.isSystemAdmin || !!user?.isCustomer

    // categories for the roles sub-menu (falls back to the first one)
    const roleCats = userRoleCategories(model?.isTech);
    const activeCatSlug = selectedAction ?? roleCats[0]?.slug;

    const fetchUser = () => {

        fetchingSet(true);
        fetchedSet(false);

        hub.Get(API_ROUTES.Users + "manage/" + selectedId, res => {
            if (res.success) {
                modelSet(res.data);
                fetchingSet(false);
                fetchedSet(true);
                bindLinks(res.data.userType);
                var ut = "";
                if (res.data.userType == UserTypes.Admin) ut = "Admins";
                else if (res.data.userType == UserTypes.Agent) ut = "Agents";
                else if (res.data.userType == UserTypes.Technician) ut = "Technicians";
                else if (res.data.userType == UserTypes.Customer) ut = "Customers";
                setPage(res.data.fullName, "Users", ut);
            }

        })
    }

    const bindLinks = (userType: UserTypes) => {
        var arr: IPageLink[] = [];
        const isVoiceMode = !!getOwner()?.freeVoiceAccount;

        arr.push({
            name: "Overview",
            icon: "chart-line",
            slug: "overview",
            default: true
        });
        arr.push({
            name: "Edit Account",
            icon: "user-pen",
            slug: "edit"
        });

        if (userType == UserTypes.Technician) {

            if (!isVoiceMode) {
                arr.push({
                    name: "Working Hours",
                    icon: "calendar-clock",
                    slug: "workinghours"
                })
            }

        if(hasRole("HasCRM"))
        {
            arr.push({
                name: "Technician Calendar",
                icon: "calendar",
                slug: "tech-calendar"
            })

            arr.push({
                name: "Address & Metro",
                icon: "map",
                slug: "address-metro"
            })

            arr.push({
                name: "Live Location",
                icon: "location-dot",
                slug: "live-location"
            })

            arr.push({
                name: "Auto Clock In & Out",
                icon: "clock-rotate-left",
                slug: "auto-clock"
            })

            arr.push({
                name: "Technician Settings",
                icon: "user-helmet-safety",
                slug: "techdetails"
            })
            
            arr.push({
                name: "Send Job Settings",
                icon: "cog",
                slug: "sendjobsettings"
            })
        }

        }

        if (!hasRole("HasCRM") && (userType == UserTypes.Technician || userType == UserTypes.Agent || userType == UserTypes.Admin)) {
            arr.push({
                name: "Calls Access",
                icon: "phone",
                slug: "callsaccess"
            })
        }

        arr.push({
            name: "Login & Security",
            icon: "key",
            slug: "security"
        })



        if (hasRole("UsersRoles") && !isVoiceMode) {
            if (userType != UserTypes.Technician) {
                arr.push({
                    name: "User Groups",
                    icon: "users",
                    slug: "groups"
                })
            }
            arr.push({
                name: "Roles & Permissions",
                icon: "lock",
                slug: "roles"
            })

        }

        // Someone else's notification settings are only the root user's, a system
        // admin's and the account owner's business — same rule the server enforces.
        if (!isVoiceMode && canManageNotifications) {
            arr.push({
                name: "Notifications",
                icon: "bell",
                slug: "notifications"
            })
        }
        arr.push({
            name: "Timezone",
            icon: "clock-two-thirty",
            slug: "timezone"
        })

        if (userType == UserTypes.Customer && user?.isSuperAdmin) {
            arr.push({
                name: "Plan",
                icon: "layer-group",
                slug: "plan"
            })
            arr.push({
                name: "Customer Settings",
                icon: "user",
                slug: "customers"
            })
            arr.push({
                name: "PBX Server",
                icon: "server",
                slug: "servers"
            })
        }


        pagesSet(p => [...arr]);

    }

    useEffect(() => {
        fetchUser();
    }, [selectedId])


    return (
        <Layout wide fitToolbar paddingBottom={0} paddingTop={0}>
            <Section fluid fit>

                <FlexRow alignItems="start">

                    <ScrollContainer className="w-100 w-lg-250px w-xl-300px border-end">
                        <div className="card card-flush" style={{ height: screen.heightDoc }}>
                            <div className="card-body pt-5 px-3">
                                <div className="menu menu-column menu-rounded menu-state-bg menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary">

                                    {
                                        pages.map((p, k) => {
                                            const isActive = (window.isNullOrEmpty(selectedPage) && p.default) || selectedPage?.toLowerCase() == p.slug;
                                            const isRolesOpen = p.slug == "roles" && isActive;

                                            const parentItem = (
                                                <div className="menu-item mb-1 mb-xxl-3">
                                                    <Link to={`/accounts/manage/${selectedId}/${p.slug}`} className={"menu-link" + (isActive ? " active" : "")}>
                                                        <span className="menu-icon">
                                                            <span className="svg-icon svg-icon-2 me-3">
                                                                <Icon icon={p.icon} type="solid" className={(isActive ? "" : " text-gray-700")} />
                                                            </span>
                                                        </span>
                                                        <span className={"menu-title fw-bold fs-7 fs-xxl-6" + (isActive ? "" : " text-gray-700")}>{p.name}</span>
                                                    </Link>
                                                </div>
                                            );

                                            if (isRolesOpen) {
                                                return (
                                                    <div key={k} className="grp-roles-open mb-1 mb-xxl-3">
                                                        {parentItem}
                                                        <div className="grp-roles-submenu">
                                                            {
                                                                roleCats.map((c, ck) => {
                                                                    const catActive = activeCatSlug == c.slug;
                                                                    return (
                                                                        <div key={ck} className="menu-item">
                                                                            <Link to={`/accounts/manage/${selectedId}/roles/${c.slug}`} className={"menu-link py-2" + (catActive ? " active" : "")}>
                                                                                <span className="menu-icon">
                                                                                    <span className="svg-icon svg-icon-3 me-3">
                                                                                        <Icon icon={c.icon as any} type="solid" className={(catActive ? "" : " text-gray-700")} />
                                                                                    </span>
                                                                                </span>
                                                                                <span className={"menu-title fw-bold fs-8 fs-xxl-7" + (catActive ? "" : " text-gray-700")}>{c.title}</span>
                                                                            </Link>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return <Fragment key={k}>{parentItem}</Fragment>;
                                        })
                                    }

                                </div>
                            </div>
                        </div>
                    </ScrollContainer>

                    <ScrollContainer className="flex-row-fluid">

                        {
                            (window.isNullOrEmpty(selectedPage) || selectedPage == "overview") && <AccountOverview userId={selectedId} model={model} />
                        }

                        {
                            selectedPage == "edit" && <AccountEditForm userId={selectedId} model={model} onUpdate={u => modelSet(u)} />
                        }

                        {
                            selectedPage == "security" && <AccountSecurity userId={selectedId} model={model} />
                        }

                        {
                            selectedPage == "groups" && model && <AccountUserGroups userId={selectedId} model={model} />
                        }

                        {
                            hasRole("UsersRoles") && selectedPage == "roles" && model && <RolesEditor user={model} categorySlug={activeCatSlug} />
                        }

                        {
                            selectedPage == "workinghours" && <TechWorkingHours tech={model} />
                        }

                        {
                            selectedPage == "tech-calendar" && <JobsCalendar techId={selectedId} />
                        }

                        {
                            selectedPage == "address-metro" && <AccountTechAddressMetro userId={selectedId} model={model} onUpdate={u => modelSet(u)} />
                        }

                        {
                            selectedPage == "live-location" && <AccountTechLiveLocation userId={selectedId} model={model} />
                        }

                        {
                            selectedPage == "auto-clock" && <AccountTechAutoClock userId={selectedId} />
                        }

                        {
                            selectedPage == "techdetails" && <AccountTechSettings userId={selectedId} model={model} onUpdate={u => modelSet(u)} />
                        }
                        {
                            selectedPage == "callsaccess" && <CallsAccess userId={selectedId} model={model} onUpdate={u => modelSet(u)} />
                        }

                        {
                            selectedPage == "sendjobsettings" && selectedId && model && <AccountTechSendJobSettings userId={selectedId} user={model} onUpdated={u => modelSet(u)} />
                        }

                        {
                            selectedPage == "notifications" && model && canManageNotifications &&
                            <NotificationsView target={model} onSaved={u => modelSet(u)} />
                        }

                        {
                            selectedPage == "timezone" && <AccountTimezone userId={selectedId} model={model} />
                        }

                        {
                            selectedPage == "customers" && user?.isSuperAdmin && model && <CustomersSettings model={model} />
                        }

                        {
                            selectedPage == "servers" && user?.isSuperAdmin && model && <AccountPBXServer user={model} />
                        }

                        {
                            selectedPage == "plan" && user?.isSuperAdmin && model && <AccountPlan model={model} />
                        }

                    </ScrollContainer>

                </FlexRow>

            </Section>
        </Layout>
    )
}